import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import Table from "../../../core/pagination/Newdatatable";
import { getApiBaseUrl } from "../Config/Config";
import { Popover, Spin } from "antd";

const AssetActivityLog = () => {
  const [auditLogData, setAuditLogData] = useState([]);
  const [loadingAuditLog, setLoadingAuditLog] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const searchInputRef = useRef(null);

  // Build the API URL
  const buildApiUrl = (page, limit, search = "") => {
    const base = `${getApiBaseUrl()}/getActivitiesAdvSearch`;
    const params = new URLSearchParams({
      pageNumber: page,
      recordslimit: limit,
    });
    if (search.trim()) {
      params.append("Search", search.trim());
    }
    return `${base}?${params.toString()}`;
  };

  const fetchLogs = async (page = 1, limit = 100, search = "") => {
    try {
      setLoadingAuditLog(true);
      const url = buildApiUrl(page, limit, search);
      const response = await fetch(url);
      const logData = await response.json();
      const list = logData?.ResultData || [];
      setAuditLogData(list || []);
      if (list?.length > 0) {
        setTotalRecords(list[0]?.TotalCount || 0);
      } else {
        setTotalRecords(0);
      }
    } catch (err) {
      console.error("Error fetching logs:", err);
      setAuditLogData([]);
      setTotalRecords(0);
    } finally {
      setLoadingAuditLog(false);
    }
  };

  // Initial load + when pagination changes
  useEffect(() => {
    fetchLogs(currentPage, pageSize, searchTerm);
  }, [currentPage, pageSize]);

  // Trigger search
  const handleSearch = () => {
    const value = searchInputRef.current?.value || "";
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page on new search
    fetchLogs(1, pageSize, value);
  };

  // Allow pressing Enter in input
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "PerformedOn",
      key: "Date",
      render: (date) => {
        if (!date) return "";
        const d = new Date(date);
        const day = String(d.getUTCDate()).padStart(2, "0");
        const month = String(d.getUTCMonth() + 1).padStart(2, "0");
        const year = d.getUTCFullYear();
        return `${day}/${month}/${year}`;
      },
      sorter: (a, b) => new Date(a.PerformedOn) - new Date(b.PerformedOn),
    },
    {
      title: "Time",
      dataIndex: "PerformedOn",
      key: "Time",
      render: (dateString) => {
        if (!dateString) return "";
        const d = new Date(dateString);
        const hours = String(d.getUTCHours()).padStart(2, "0");
        const minutes = String(d.getUTCMinutes()).padStart(2, "0");
        // const seconds = String(d.getUTCSeconds()).padStart(2, "0");
        return `${hours}:${minutes}`;
      },
      sorter: (a, b) =>
        new Date(a.PerformedOn).getTime() - new Date(b.PerformedOn).getTime(),
    },

    {
      title: "User ID",
      dataIndex: "username",
      key: "username",
      sorter: (a, b) => a.username - b.username,
    },
    // {
    //   title: "Description",
    //   dataIndex: "Notes",
    //   key: "Description",
    //   sorter: (a, b) => a.Notes.localeCompare(b.Notes),
    // },
    {
      title: "Description",
      dataIndex: "Notes",
      key: "Description",
      sorter: (a, b) => a.Notes?.localeCompare(b.Notes),
      render: (text) => (
        <Popover
          content={
            <div
              style={{
                maxWidth: "70vw",
                maxHeight: "70vh",
                overflowY: "auto",
                whiteSpace: "normal",
                wordBreak: "break-word",
              }}
            >
              {text}
            </div>
          }
          trigger="click"
          placement="top"
        >
          <div
            className="text-truncate"
            style={{ maxWidth: "35vw", cursor: "pointer" }}
          >
            {text}
          </div>
        </Popover>
      ),
    },
    {
      title: "Status",
      dataIndex: "IsSucces",
      key: "Status",
      render: (status) => (
        <span style={{ color: status ? "green" : "red" }}>
          {status ? "Success" : "Fail"}
        </span>
      ),
      sorter: (a, b) => a.IsSucces - b.IsSucces,
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/assetmgt/dashboard">
                      <i className="fas fa-home fa-lg" />
                    </Link>
                  </li>
                  <h4 className="breadcrumb-item " aria-current="page">
                    Audit Trail
                  </h4>
                </ol>
              </nav>
              <h6>Track your Logs</h6>
            </div>
          </div>
        </div>

        <div className="card table-list-card no-search">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <div className="search-set">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search logs..."
                  ref={searchInputRef}
                  onKeyPress={handleKeyPress}
                  defaultValue={searchTerm}
                  aria-label="Search logs"
                />
                <button
                  className="btn btn-primary btn-sm"
                  type="button"
                  onClick={handleSearch}
                  disabled={loadingAuditLog}
                >
                  <i className="fas fa-search"></i>
                </button>
              </div>
            </div>

            <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3"></div>
          </div>

          <div className="card-body ">
            <div className="table-responsive">
              {loadingAuditLog ? (
                <div className="text-center my-4">
                  <Spin size="large" />
                  <p>Loading data...</p>
                </div>
              ) : auditLogData.length > 0 ? (
                <Table
                  columns={columns}
                  dataSource={auditLogData}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  totalRecords={totalRecords}
                  onPageChange={(page, size) => {
                    setCurrentPage(page);
                    setPageSize(size);
                  }}
                />
              ) : (
                <p className="text-center my-4">
                  {searchTerm
                    ? "No results found for your search."
                    : "No data available"}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetActivityLog;
